import React, { useState, useEffect, useMemo, useCallback } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useProperty,
  usePropertyField,
  usePropertyAddressUrl,
  useCollectionBinding,
  useTokens,
  usePage,
} from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import { useComfyCtaStyles } from '../hooks/useComfyCtaStyles';
// The Header builds its nav from the raw page metadata (name / path /
// position). Our local pages.json holds the full per-page metadata for
// this company theme so the horizontal nav resolves in isolation — the
// runtime does not currently expose a merged pages API at browser
// runtime, so the raw JSON here is the source of truth for what appears
// in the top nav. Child property sites can still override any single
// field (visibility, name, position, ...) via their own pages.json.
import localPagesData from '../pages.json';
const pagesData = localPagesData as unknown as Record<string, PageEntry>;
import { VibesitesImage } from '@vibesites/ui-primitives';
import { contrastRatio, pickHighestContrast } from '../utils/luminance';
import { handleScheduleTourClick, scheduleTourHref } from '../utils/scheduleTour';


// ============================================================================
// Dynamic Navigation from pages.json
// ============================================================================

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
  noindex?: boolean;
  /** When set, this page is rendered as a sub-nav item under the parent
      page's nav entry (a dropdown on desktop, indented row on mobile).
      Not part of the linter's KNOWN_FIELDS set, but retained here so a
      future opt-in doesn't require any Header changes. */
  parent?: string;
}

/** Page aliases that should never appear in the main navigation */
const NAV_EXCLUDED_ALIASES = new Set([
  'home',
  'privacy-policy',
  'sitemap',
  'accessibility',
  'apply-now',
  'thank-you',
  'resident-access',
  'schedule-a-tour',
  'hyperlocal',
  'reviews',
]);

interface NavLink {
  label: string;
  href: string;
  /** Sub-nav items rendered as a dropdown on desktop and indented rows on
      mobile. Populated when other pages declare `parent: <this alias>`. */
  children?: NavLink[];
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

/**
 * Build navigation links from pages.json.
 * Only includes pages that are visible and not in the exclusion list.
 * Sorted by position. Pages declaring `parent: <alias>` are nested as
 * `children` of their parent entry rather than appearing at the top level.
 * Orphaned children (parent missing or hidden) fall back to the top level
 * so a page is never silently dropped from the nav.
 */
function buildNavLinks(hasVirtualTours: boolean, hasInteractiveLeasing: boolean): NavLink[] {
  const eligible = Object.entries(pagesData)
    .filter(([alias, page]) => {
      if (NAV_EXCLUDED_ALIASES.has(alias)) return false;
      if (page.visible === false) return false;
      if (page.template) return false;
      if (alias === 'virtual-tour' && !hasVirtualTours) return false;
      if (alias === 'interactive-map' && !hasInteractiveLeasing) return false;
      return true;
    })
    .sort(([, a], [, b]) => a.position - b.position);

  const eligibleAliases = new Set(eligible.map(([alias]) => alias));
  const linksByAlias = new Map<string, NavLink>();
  const topLevel: NavLink[] = [];

  for (const [alias, page] of eligible) {
    linksByAlias.set(alias, { label: page.name, href: page.path });
  }

  for (const [alias, page] of eligible) {
    const link = linksByAlias.get(alias)!;
    if (page.parent && eligibleAliases.has(page.parent) && page.parent !== alias) {
      const parentLink = linksByAlias.get(page.parent)!;
      (parentLink.children ??= []).push(link);
    } else {
      topLevel.push(link);
    }
  }

  return topLevel;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_white_logo', suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/apply-now' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const residentAccessLinkDef: SlotGroupDefinition = {
  label: 'Resident Access Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Portal' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule Tour Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address Link',
  fields: {
    text: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      suggestedFields: ['property_full_address', 'property_address_1'],
      defaultValue: '123 Main Street',
    },
    url: { label: 'URL', type: 'url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const phoneLinkDef: SlotGroupDefinition = {
  label: 'Phone Link',
  fields: {
    text: {
      label: 'Phone Text',
      type: 'text',
      defaultField: 'property_call_tracking_phone',
    },
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// SVG Icon Components
// ============================================================================

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
}

function digitsOnly(raw: string | null | undefined): string {
  if (!raw) return '';
  return raw.replace(/[^+\d]/g, '');
}

function toTelUrl(raw: string | null | undefined): string {
  if (!raw) return '';
  if (raw.startsWith('tel:')) return raw;
  const digits = digitsOnly(raw);
  return digits ? `tel:${digits}` : raw;
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

// ============================================================================
// Office hours helpers — mirrors the parent Footer's collection shape so the
// drawer surfaces the same weekday grouping. Kept local (rather than pulling
// listOfficeHours from Footer.tsx) so the header doesn't depend on Footer's
// public surface — Footer's helper could shift without notice, and the drawer
// only needs the collapsed Mon–Fri / weekend view shown in the reference.
// ============================================================================

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

const HOURS_DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const HOURS_DAY_SHORT: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu',
  fri: 'Fri', sat: 'Sat', sun: 'Sun',
};
const HOURS_DAY_FULL: Record<string, string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};

function formatHoursTime(raw: string | null | undefined): string {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  const match = trimmed.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return trimmed;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

function normalizeHoursDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function hoursDisplay(entry: OfficeHour | undefined): string {
  if (!entry) return 'Closed';
  const open = (entry.open_time || '').trim();
  const close = (entry.close_time || '').trim();
  if (!open || !close || /^(closed|null|false|0)$/i.test(open) || /^(closed|null|false|0)$/i.test(close)) {
    return 'Closed';
  }
  return `${formatHoursTime(open)} - ${formatHoursTime(close)}`;
}

/**
 * Collapses consecutive days that share the same open window into a single
 * range row (e.g. Mon–Fri, Sat–Sun) so the drawer stays compact.
 */
function summarizeOfficeHours(raw: OfficeHour[]): Array<{ label: string; hours: string }> {
  const byDay = new Map<string, OfficeHour>();
  for (const entry of raw) {
    if (!entry?.day) continue;
    const key = normalizeHoursDay(entry.day);
    if (HOURS_DAY_ORDER.includes(key) && !byDay.has(key)) byDay.set(key, entry);
  }

  const entries = HOURS_DAY_ORDER.map((key) => ({
    key,
    hours: hoursDisplay(byDay.get(key)),
  }));

  const rows: Array<{ label: string; hours: string }> = [];
  let i = 0;
  while (i < entries.length) {
    let j = i;
    while (j + 1 < entries.length && entries[j + 1].hours === entries[i].hours) {
      j += 1;
    }
    const label =
      j > i
        ? `${HOURS_DAY_SHORT[entries[i].key]} - ${HOURS_DAY_SHORT[entries[j].key]}`
        : HOURS_DAY_FULL[entries[i].key];
    rows.push({ label, hours: entries[i].hours });
    i = j + 1;
  }
  return rows;
}

// ============================================================================
// Mobile Nav Link
// ============================================================================

function MobileNavLink({
  link,
  onNavigate,
  isActive,
}: {
  link: NavLink;
  onNavigate: () => void;
  isActive?: boolean;
  /** Retained for parity with desktop but intentionally unused — children
      render flush with parents in the drawer because the visual offset
      reads as misaligned noise in a vertical list. */
  isChild?: boolean;
}) {
  return (
    <a
      href={link.href}
      onClick={onNavigate}
      className={`menu-nav-link block py-2.5 font-body text-base font-normal tracking-wide text-white transition-colors${
        isActive ? ' menu-nav-link--active' : ''
      }`}
    >
      {/* Inline-flex span holds the text (and optional dropdown chevron)
          so the ::after underline in menu CSS can anchor to the label
          width — a text-hugging underline reads more clearly than a
          fixed-length bar for long labels like "Neighborhood". */}
      <span className="menu-nav-link__label inline-flex items-center gap-2">
        {link.label}
        {link.children?.length ? (
          <ChevronDownIcon className="w-4 h-4 opacity-70" />
        ) : null}
      </span>
    </a>
  );
}

// ============================================================================
// Desktop Nav Link (horizontal bar, lg+)
// ============================================================================

function DesktopNavLink({
  link,
  isActive,
  isNavActive,
}: {
  link: NavLink;
  isActive: boolean;
  isNavActive: (href: string) => boolean;
}) {
  const hasChildren = !!link.children?.length;

  if (!hasChildren) {
    return (
      <a
        href={link.href}
        className={`header-nav-link${isActive ? ' header-nav-link--active' : ''}`}
      >
        {link.label}
      </a>
    );
  }

  return (
    <div className="header-nav-group relative group">
      <a
        href={link.href}
        className={`header-nav-link header-nav-link--parent${isActive ? ' header-nav-link--active' : ''}`}
      >
        <span className="inline-flex items-center gap-1">
          {link.label}
          <ChevronDownIcon className="w-3.5 h-3.5 opacity-70" />
        </span>
      </a>
      <div className="nav-more-dropdown">
        {link.children!.map((child) => (
          <a
            key={child.href}
            href={child.href}
            className={`nav-more-dropdown-link${isNavActive(child.href) ? ' header-nav-link--active' : ''}`}
          >
            {child.label}
          </a>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Main Header Component
// ============================================================================
// STRUCTURE: Two stacked bars.
//   1. .header-top-strip — thin band pinned above the main nav. Uses the
//      theme's secondary color as its background and holds address /
//      phone / Resident Portal + Apply Now on desktop. Slides up and
//      collapses on scroll so it never re-appears once the visitor moves
//      past the hero.
//   2. .header-bar — the main navigation. Transparent by default so the
//      hero photo shows through at the top of the page; transitions to a
//      solid white surface on scroll (with the address strip removed).
//      Holds the logo, horizontal nav links, and a Schedule a Tour CTA.
// ============================================================================

export function Header() {
  // Keeps shared CTA hover text contrast in sync site-wide (Header is on every page).
  useComfyCtaStyles();

  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Scroll detection for transparent → solid transition on the main bar.
  // Same debounce/hysteresis pattern used elsewhere so the header doesn't
  // thrash between states around the trigger point.
  useEffect(() => {
    let ticking = false;
    const SCROLL_AT = 20;
    const UNSCROLL_AT = 8;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        setScrolled(prev => {
          if (!prev && y > SCROLL_AT) return true;
          if (prev && y < UNSCROLL_AT) return false;
          return prev;
        });
        ticking = false;
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Resolve the current page synchronously via the runtime context.
  const pageCtx = usePage();

  const tokens = useTokens();

  // Scrolled state: main bar is solid WHITE, so we use the theme's dark
  // text color for links / logo. When the property doesn't have a dark
  // primary logo, fall back to the white one over the scrolled band. The
  // scrolled bar in this theme is intentionally always white (per design
  // reference); we don't paint it with primary/secondary tokens.
  const scrolledTextColor = useMemo(() => {
    const background = '#ffffff';
    const textBase = tokens?.color?.text || '#111111';
    const primary = tokens?.color?.primary;
    const candidates = [textBase, primary].filter(Boolean) as string[];
    return pickHighestContrast(background, candidates) || textBase;
  }, [tokens]);

  // Top-strip color pick. The strip is filled with the secondary token,
  // so the address / phone / util links pick whichever of
  // textOnSecondary / white / base-text has the highest WCAG contrast
  // against that surface. Prevents unreadable label-on-taupe combos when
  // a property tunes secondary to an odd hue.
  const [topStripTextColor, topStripBgIsDark] = useMemo(() => {
    const secondary = tokens?.color?.secondary;
    if (!secondary) return ['#ffffff', true] as const;
    const white = '#ffffff';
    const textBase = tokens?.color?.text || '#111111';
    const onSecondary = tokens?.color?.textOnSecondary;
    const candidates = [onSecondary, white, textBase].filter(Boolean) as string[];
    const picked = pickHighestContrast(secondary, candidates) || white;
    const isDark = contrastRatio(secondary, white) > contrastRatio(secondary, textBase);
    return [picked, isDark] as const;
  }, [tokens]);

  // Secondary-bar accent for the Schedule a Tour CTA when it sits over
  // the scrolled white bar — matches the ref image's taupe button.
  const secondaryAccent = 'var(--vibe-color-secondary)';
  const secondaryOnAccent = 'var(--vibe-color-text-on-secondary)';

  // Virtual tours + interactive-map — control nav-link visibility.
  const virtualTours = useCollectionBinding<VirtualTour>('header.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  const propertyForLeasing = useProperty() as Record<string, unknown>;
  const apartmentSyncJsId = propertyForLeasing?.custom_apartmentsync_javascript_id as
    | string
    | null
    | undefined;
  const apartmentSyncConfigId = propertyForLeasing?.custom_apartmentsync_config_id as
    | string
    | null
    | undefined;
  const hasInteractiveLeasing =
    !!(apartmentSyncJsId && String(apartmentSyncJsId).trim()) &&
    !!(apartmentSyncConfigId && String(apartmentSyncConfigId).trim());

  const navLinks = useMemo(
    () => buildNavLinks(hasVirtualTours, hasInteractiveLeasing),
    [hasVirtualTours, hasInteractiveLeasing],
  );

  const currentPath = pageCtx?.path
    || (typeof window !== 'undefined' ? window.location.pathname : '/');

  const isNavActive = useCallback((href: string) => {
    const normalized = currentPath.endsWith('/') && currentPath !== '/'
      ? currentPath.slice(0, -1)
      : currentPath;
    const target = href.endsWith('/') && href !== '/'
      ? href.slice(0, -1)
      : href;
    if (target === '/') return normalized === '/';
    return normalized === target || normalized.startsWith(`${target}/`);
  }, [currentPath]);

  // Property featured image — used as the dimmed backdrop behind the
  // slide-in nav drawer (an image sits behind a semi-transparent black wash).
  const menuBgImage = usePropertyField('property_featured_image');
  const propertyAddressUrl = usePropertyAddressUrl();
  const callTrackingPhone = usePropertyField('property_call_tracking_phone') as string | null | undefined;
  const propertyPhone = usePropertyField('property_phone') as string | null | undefined;
  const defaultTelUrl = toTelUrl(callTrackingPhone) || toTelUrl(propertyPhone);

  const officeHoursRaw = useCollectionBinding<OfficeHour>('header.office_hours', {
    collection: 'office_hours',
  });
  const officeHoursRows = useMemo(
    () => summarizeOfficeHours(officeHoursRaw || []),
    [officeHoursRaw],
  );

  // Slot groups
  const logoRaw = useSlotGroup('header.logo', logoSlotDef);
  const monoWhiteLogo = usePropertyField('property_mono_white_logo');
  const monoBlackLogo = usePropertyField('property_mono_black_logo');

  // Logo swap on scroll:
  //   - Transparent hero  → mono-white variant (falls back to the slot's
  //                         resolved asset when the property doesn't ship
  //                         a dedicated white).
  //   - Scrolled white bar → mono-black variant.
  // Two distinct assets → cross-fade the stack. If mono-black is missing
  // we drop back to a single-asset render with a CSS brightness(0) filter
  // on scroll so a mono-white logo still reads as solid dark on the
  // white bar instead of silhouetting into invisibility.
  const headerLogoWhite = monoWhiteLogo || logoRaw.image;
  const headerLogoBlack = monoBlackLogo || null;
  const headerLogoHasSwap = !!(headerLogoWhite && headerLogoBlack && headerLogoWhite !== headerLogoBlack);

  // Single-asset fallback: whichever of white / black we do have.
  const singleLogoSource = headerLogoWhite || headerLogoBlack || null;

  const applyNowLink = useSlotGroup('header.apply_now_link', applyNowLinkDef);
  const residentAccessLink = useSlotGroup('header.resident_access_link', residentAccessLinkDef);
  const scheduleTourLink = useSlotGroup('header.schedule_tour_link', scheduleTourLinkDef);
  const addressLink = useSlotGroup('header.address_link', addressLinkDef);
  const phoneLink = useSlotGroup('header.phone_link', phoneLinkDef);

  // Address renders as a single link. When the editor hasn't authored text,
  // fall back to the property's full address; URL falls back to the shared
  // property map URL.
  const propertyFullAddress = usePropertyField('property_full_address') as string | null | undefined;

  // Drop a trailing ", USA" / " USA" so the display doesn't carry the country
  // tag some property records append.
  const stripUsa = (s: string) => s.replace(/[,\s]+USA\.?\s*$/i, '').trim();

  const addressText = stripUsa(addressLink.text || propertyFullAddress || '123 Main Street');
  const addressHref = addressLink.url || propertyAddressUrl || '#';
  const addressTarget = addressLink._target || '_blank';
  const phoneHref = toTelUrl(phoneLink.url) || defaultTelUrl;
  const phoneDisplay = formatPhone(phoneLink.text) || formatPhone(callTrackingPhone) || formatPhone(propertyPhone) || '555-555-5555';

  // Google Fonts — brand-wide switch to Montserrat. Header renders on every
  // page so this doubles as the site-wide font loader; Hero / HeroSolidBG
  // still guard-load the same face for SSG'd pages where Header hydration
  // hasn't run yet.
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="montserrat"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400;1,700&display=swap';
    link.setAttribute('data-vibe-font', 'montserrat');
    document.head.appendChild(link);
  }, []);

  // Lock body scroll when mobile menu is open, and mark the document so
  // third-party overlays (EliseAI chat) can be tucked below the drawer.
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    document.body.classList.toggle('header-menu-open', menuOpen);
    return () => {
      document.body.style.overflow = '';
      document.body.classList.remove('header-menu-open');
    };
  }, [menuOpen]);

  // Desktop (xl+) uses the horizontal nav, not the hamburger drawer. If the
  // drawer was left open on tablet/mobile and the viewport (or device
  // preview) switches to desktop, close it so the full-bleed sheet doesn't
  // linger over the desktop header.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mq = window.matchMedia('(min-width: 1280px)');
    const syncDesktopNav = () => {
      if (mq.matches) setMenuOpen(false);
    };
    syncDesktopNav();
    mq.addEventListener('change', syncDesktopNav);
    return () => mq.removeEventListener('change', syncDesktopNav);
  }, []);

  const closeMobileMenu = () => setMenuOpen(false);

  return (
    <>
      {/* No spacer — transparent header overlays hero content */}

      <header
        className={`fixed left-0 right-0 w-full ${menuOpen ? 'z-[100000]' : 'z-50'}${scrolled ? ' header-scrolled' : ' header-transparent'}${topStripBgIsDark ? ' header-top-strip--on-dark' : ' header-top-strip--on-light'}`}
        style={{
          top: 0,
          // Header sits below the SpecialOfferBanner (which is fixed at
          // top:0 z-60). Pad the whole header down by the banner height so
          // the top strip / main bar never disappear underneath it. The
          // var drops to 0 automatically when the banner scroll-hides.
          paddingTop: 'var(--offer-banner-height, 0px)',
          // Text colors picked from luminance against each strip's surface.
          ['--aion-header-scrolled-text' as string]: scrolledTextColor,
          ['--aion-header-top-strip-text' as string]: topStripTextColor,
          ['--aion-header-cta-accent' as string]: secondaryAccent,
          ['--aion-header-cta-on-accent' as string]: secondaryOnAccent,
        }}
      >
        {/* ============================================================== */}
        {/* Top strip — address / phone / Resident Portal + Apply Now.     */}
        {/* Filled with the secondary color; hidden once the user scrolls  */}
        {/* past the hero (collapses height so it doesn't re-appear).      */}
        {/* Hidden below md — small viewports get the drawer instead.      */}
        {/* ============================================================== */}
        <div className="header-top-strip">
          <div className="header-top-strip-inner">
            <EditableSlotGroup
              groupPath="header.address_link"
              definition={addressLinkDef}
              className="header-top-address-slot"
            >
              <span className="header-top-address">
                <a
                  href={addressHref}
                  target={addressTarget}
                  rel="noopener noreferrer"
                  className="header-top-address-link"
                >
                  {addressText}
                </a>
              </span>
            </EditableSlotGroup>

            <div className="header-top-actions">
              <EditableSlotGroup
                groupPath="header.phone_link"
                definition={phoneLinkDef}
                className="header-top-phone-slot"
              >
                <a
                  href={phoneHref || '#'}
                  target={phoneLink._target || '_self'}
                  className="header-top-phone dniphone dniphonehref"
                >
                  {phoneDisplay}
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                <a
                  href={residentAccessLink.url || '/resident-access'}
                  target={residentAccessLink._target || '_self'}
                  className="header-top-util"
                >
                  {residentAccessLink.label || 'Resident Portal'}
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                <a
                  href={applyNowLink.url || '/apply-now'}
                  target={applyNowLink._target || '_self'}
                  className="header-top-util"
                >
                  {applyNowLink.label || 'Apply Now'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>
        </div>

        {/* ============================================================== */}
        {/* Main navigation bar                                            */}
        {/* Transparent by default, solid white on scroll.                 */}
        {/* ============================================================== */}
        <div className="header-bar">
          <div className="header-bar-inner">
            <div className="header-bar-row flex items-center">
              {/* Logo */}
              <EditableSlotGroup groupPath="header.logo" definition={logoSlotDef} className="aion-header-logo-slot">
                <a href="/" className="flex-shrink-0">
                  {singleLogoSource ? (
                    headerLogoHasSwap ? (
                      <span className="header-bar-logo-stack inline-flex items-center">
                        <VibesitesImage
                          url={headerLogoWhite}
                          alt={logoRaw.alt || 'Community Logo'}
                          className="header-bar-logo header-bar-logo--light"
                        />
                        {/* Decorative overlay — the light logo above already
                            carries the accessible name, so the dark variant
                            gets an empty alt to avoid double-announcing. */}
                        <VibesitesImage
                          url={headerLogoBlack}
                          alt=""
                          className="header-bar-logo header-bar-logo--dark"
                        />
                      </span>
                    ) : (
                      // Single-asset fallback: the property only ships a
                      // mono-white (or has no mono-black to swap into).
                      // Apply `.header-bar-logo--auto-darken` so CSS can
                      // filter the mark to solid dark on the scrolled
                      // white bar — without this, a mono-white logo
                      // silhouettes into invisibility once the bar turns
                      // white on scroll.
                      <VibesitesImage
                        url={singleLogoSource}
                        alt={logoRaw.alt || 'Community Logo'}
                        className="header-bar-logo header-bar-logo--auto-darken"
                      />
                    )
                  ) : (
                    <div className="font-heading text-xl lg:text-2xl font-bold text-[var(--vibe-color-primary)]">
                      The Vantage
                    </div>
                  )}
                </a>
              </EditableSlotGroup>

              {/* Desktop horizontal navigation (xl+). Below xl the drawer
                  surfaces these links — seven long labels + Schedule a Tour
                  do not fit reliably at the lg (1024) breakpoint. */}
              <nav
                className="header-desktop-nav hidden xl:flex flex-1 items-center justify-end pl-4 pr-2 min-w-0"
                aria-label="Primary navigation"
              >
                {/* Home is excluded — logo links to `/`; an extra Home link
                    crowded the bar and pushed Schedule a Tour off-screen. */}
                {navLinks.map((link) => (
                  <DesktopNavLink
                    key={link.href}
                    link={link}
                    isActive={isNavActive(link.href)}
                    isNavActive={isNavActive}
                  />
                ))}
              </nav>

              {/* Right group — Schedule a Tour CTA + hamburger. */}
              <div className="header-bar-actions flex items-center gap-3 lg:gap-4 flex-shrink-0 ml-auto">
                <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
                  <a
                    href={scheduleTourHref(scheduleTourLink.url || '/schedule-a-tour')}
                    data-schedule-tour-href={scheduleTourLink.url || '/schedule-a-tour'}
                    target={scheduleTourLink._target || '_self'}
                    onClick={handleScheduleTourClick}
                    className="header-schedule-btn hidden sm:inline-flex"
                  >
                    {scheduleTourLink.label || 'Schedule a Tour'}
                  </a>
                </EditableSlotGroup>

                {/* Hamburger — opens the full-screen drawer. Below xl the
                    desktop nav collapses, so the drawer is the sole nav
                    access below that breakpoint. */}
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="xl:hidden relative w-10 h-10 flex items-center justify-center"
                  aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                  aria-expanded={menuOpen}
                >
                  <span
                    className={`absolute block w-6 h-0.5 header-hamburger-bar transition-all duration-300 ${
                      menuOpen ? 'rotate-45' : '-translate-y-2'
                    }`}
                  />
                  <span
                    className={`absolute block w-6 h-0.5 header-hamburger-bar transition-all duration-300 ${
                      menuOpen ? 'opacity-0' : ''
                    }`}
                  />
                  <span
                    className={`absolute block w-6 h-0.5 header-hamburger-bar transition-all duration-300 ${
                      menuOpen ? '-rotate-45' : 'translate-y-2'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================== */}
        {/* Menu overlay — near-full-width sheet on mobile only. From sm  */}
        {/* through the hamburger range (< xl) the drawer is a compact     */}
        {/* ~22rem sheet (matching smaller-desktop) so unused navy on the  */}
        {/* right stays minimal. Backdrop dims + dismisses.                */}
        {/* ============================================================== */}
        <div
          aria-hidden={!menuOpen}
          className={`menu-backdrop fixed inset-0 z-[100000] bg-black/40 transition-opacity duration-300 xl:hidden ${
            menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
          onClick={closeMobileMenu}
        />

        <div
          aria-hidden={!menuOpen}
          className={`mobile-menu-panel fixed right-0 top-0 bottom-0 w-[calc(100%-3rem)] overflow-hidden transition-transform duration-300 ease-out z-[100001] flex flex-col xl:hidden ${
            menuOpen ? 'translate-x-0 mobile-menu-open' : 'translate-x-full pointer-events-none'
          }`}
        >
          <div className="menu-bg" aria-hidden="true">
            {menuBgImage ? (
              <VibesitesImage
                url={menuBgImage}
                alt=""
                objectFit="cover"
                className="w-full h-full object-cover"
              />
            ) : null}
          </div>
          <div className="menu-overlay" aria-hidden="true" />

          {/* Header row — reference design has no logo in the drawer, only
              the close affordance pinned to the top-right. Keeping the
              wrapper (rather than dropping to a bare button) preserves the
              flex-shrink-0 layout hook and the safe-area padding the
              scroll region relies on. */}
          <div className="menu-panel-header relative z-20 flex items-center justify-end px-6 pt-6">
            <button
              type="button"
              onClick={closeMobileMenu}
              className="menu-close-btn"
              aria-label="Close menu"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>

          <div className="menu-scroll relative z-10 flex-1 overflow-y-auto pb-10">
            <div className="menu-layout flex flex-col px-6 pt-6 gap-10">
              <nav role="navigation" aria-label="Main navigation" className="menu-nav">
                {navLinks.map((link) => (
                  <React.Fragment key={link.href}>
                    <MobileNavLink
                      link={link}
                      onNavigate={closeMobileMenu}
                      isActive={isNavActive(link.href)}
                    />
                    {link.children?.map((child) => (
                      <MobileNavLink
                        key={child.href}
                        link={child}
                        onNavigate={closeMobileMenu}
                        isActive={isNavActive(child.href)}
                        isChild
                      />
                    ))}
                  </React.Fragment>
                ))}
                {/* Drawer CTA stack — Schedule a Tour is the featured
                    primary action (accent-filled) and Apply Now /
                    Resident Portal follow as secondary buttons. All
                    three share the .menu-drawer-cta base so padding,
                    typography, and radius stay in lockstep. */}
                <div className="menu-drawer-cta-row">
                  <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
                    <a
                      href={scheduleTourHref(scheduleTourLink.url || '/schedule-a-tour')}
                      data-schedule-tour-href={scheduleTourLink.url || '/schedule-a-tour'}
                      target={scheduleTourLink._target || '_self'}
                      onClick={(e) => {
                        handleScheduleTourClick(e);
                        closeMobileMenu();
                      }}
                      className="menu-drawer-cta menu-drawer-cta--primary"
                    >
                      {scheduleTourLink.label || 'Schedule a Tour'}
                    </a>
                  </EditableSlotGroup>
                  <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                    <a
                      href={applyNowLink.url || '/apply-now'}
                      target={applyNowLink._target || '_self'}
                      onClick={closeMobileMenu}
                      className="menu-drawer-cta menu-drawer-cta--secondary"
                    >
                      {applyNowLink.label || 'Apply Now'}
                    </a>
                  </EditableSlotGroup>
                  <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                    <a
                      href={residentAccessLink.url || '/resident-access'}
                      target={residentAccessLink._target || '_self'}
                      onClick={closeMobileMenu}
                      className="menu-drawer-cta menu-drawer-cta--secondary"
                    >
                      {residentAccessLink.label || 'Resident Portal'}
                    </a>
                  </EditableSlotGroup>
                </div>
              </nav>

              {/* Contact column — Call Us / Visit Us / Office Hours stacks
                  below the CTAs for the entire hamburger range (mobile
                  through smaller desktop). Desktop xl+ uses the bar nav. */}
              <div className="menu-contact">
                <div className="menu-info-stack">
                  {/* Call Us */}
                  <div className="menu-info-block">
                    <div className="menu-info-label">Call Us</div>
                    <EditableSlotGroup groupPath="header.phone_link" definition={phoneLinkDef}>
                      <a
                        href={phoneHref || '#'}
                        target={phoneLink._target || '_self'}
                        className="menu-info-value dniphone dniphonehref"
                      >
                        {phoneDisplay}
                      </a>
                    </EditableSlotGroup>
                  </div>

                  {/* Visit Us */}
                  <div className="menu-info-block">
                    <div className="menu-info-label">Visit Us</div>
                    <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef}>
                      <a
                        href={addressHref}
                        target={addressTarget}
                        rel="noopener noreferrer"
                        className="menu-info-value"
                      >
                        {addressText}
                      </a>
                    </EditableSlotGroup>
                  </div>

                  {/* Office Hours */}
                  <div className="menu-info-block menu-info-hours">
                    <div className="menu-info-label">Office Hours</div>
                    <dl className="menu-hours-list">
                      {officeHoursRows.map((row) => (
                        <div key={row.label} className="menu-hours-row">
                          <dt className="menu-hours-day">{row.label}</dt>
                          <dd className="menu-hours-time">{row.hours}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Brand-wide typography reset. Header renders on every page, so this
          `<style>` block defines the site-wide defaults for the bare `p`,
          `h1`, `h2`, and `h3` elements per the Aion type spec (Montserrat,
          specific weights/sizes, line-height 1). Kept as a separate block
          from the header-specific rules below so it can be maintained /
          audited independently. Only font-family, font-weight, font-size,
          and line-height are set — color, letter-spacing, text-align, and
          text-direction are intentionally left alone so per-component
          treatments (hero titles, footer copy, form labels, etc.) keep
          their existing formatting. Class-scoped component rules elsewhere
          in the theme naturally win on specificity when they need to
          diverge from the base spec. */}
      <style>{`
        html, body {
          font-family: "Montserrat", var(--vibe-font-body, sans-serif);
          font-weight: 300;
          font-size: 16px;
          line-height: 1;
        }
        p {
          font-family: "Montserrat", var(--vibe-font-body, sans-serif);
          font-weight: 300;
          font-size: 16px;
          line-height: 1;
        }
        h1 {
          font-family: "Montserrat", var(--vibe-font-heading, sans-serif);
          font-weight: 400;
          font-size: 75px;
          line-height: 1;
        }
        h2 {
          font-family: "Montserrat", var(--vibe-font-heading, sans-serif);
          font-weight: 400;
          font-size: 36px;
          line-height: 1;
        }
        h3 {
          font-family: "Montserrat", var(--vibe-font-heading, sans-serif);
          font-weight: 400;
          font-size: 30px;
          line-height: 1;
        }
      `}</style>

      <style>{`
        /* ================================================================ */
        /* Top strip — address / phone / Resident Portal + Apply Now       */
        /* ================================================================ */
        .header-top-strip {
          background-color: var(--vibe-color-secondary);
          color: var(--aion-header-top-strip-text, #fff);
          overflow: hidden;
          max-height: 200px;
          opacity: 1;
          transition: max-height 0.35s ease, opacity 0.25s ease, padding 0.35s ease;
        }
        /* Mobile: keep the top strip visible but restrict it to a single
           centered address line on a light neutral surface (contact +
           util actions still live in the drawer). Matches the mobile
           reference where the address reads as a subtle info band above
           the transparent hero header. */
        @media (max-width: 767px) {
          .header-top-strip {
            background-color: #ebebeb;
            color: var(--vibe-color-primary, var(--vibe-color-text, #111));
          }
        }
        .header-top-strip-inner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1.5rem;
          padding: 0.65rem 3.125rem;
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          font-weight: 400;
        }
        @media (min-width: 1280px) {
          .header-top-strip-inner {
            padding-left: 4rem;
            padding-right: 4rem;
          }
        }
        .header-top-address,
        .header-top-phone {
          color: var(--aion-header-top-strip-text, #fff);
          text-decoration: none;
          white-space: nowrap;
          transition: opacity 0.2s ease;
        }
        .header-top-phone:hover {
          opacity: 0.82;
          /* Preserve the base color on hover — on mobile the strip is a
             light band with dark text, so we no longer force the fallback
             white here. */
          color: inherit;
        }
        /* Apply Now / Resident Portal render as small gold pill CTAs so
           they share the Hero CTA's color palette
           (--vibe-color-secondary bg, --vibe-color-text-on-secondary fg,
           hover swaps bg to surface with auto-contrast text via
           --comfy-cta-fg-hover). Padding + radius are scaled down from
           .comfy-cta / .header-schedule-btn so the pills still fit the
           compact top-strip row alongside the address / phone. */
        .header-top-util {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.4rem 0.95rem;
          background: var(--vibe-color-secondary, #c9a961);
          color: var(--vibe-color-text-on-secondary, #ffffff);
          text-decoration: none;
          white-space: nowrap;
          border-radius: 10px;
          transition: background-color 0.25s ease, color 0.25s ease;
        }
        .header-top-util:hover,
        .header-top-util:focus-visible {
          background: var(--comfy-cta-bg-hover, var(--vibe-color-surface, #f5f5f5));
          color: var(--comfy-cta-fg-hover, #1a1a1a);
        }
        /* Top strip is two columns: address on the left, phone + util
           CTAs grouped on the right (matches Comfy sites like Cheltenham
           Station). Nested .header-top-address / .header-top-phone only
           handle typography + color. */
        .header-top-address-slot {
          flex: 1 1 0;
          min-width: 0;
          display: flex;
          align-items: center;
          justify-content: flex-start;
        }
        .header-top-address {
          text-align: left;
          font-weight: 400;
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
        }
        /* Each of the two address links inherits the strip's text color and
           reuses the same subtle hover fade. */
        .header-top-address-link {
          color: inherit;
          text-decoration: none;
          transition: opacity 0.2s ease;
        }
        .header-top-address-link:hover {
          opacity: 0.82;
          color: inherit;
        }
        .header-top-phone-slot {
          flex: 0 0 auto;
          display: flex;
          align-items: center;
        }
        .header-top-phone {
          text-align: right;
          font-weight: 400;
        }
        .header-top-actions {
          flex: 0 0 auto;
          margin-left: auto;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 1.5rem;
        }
        /* Tablet (768–1023): hide the address so the phone / util CTAs
           stay legible. Address is still available in the drawer's
           contact column. Scoped to min-width:768 so the mobile band
           (which shows *only* the address) isn't affected. */
        @media (min-width: 768px) and (max-width: 1023px) {
          .header-top-address-slot {
            display: none;
          }
          .header-top-actions {
            flex: 1 1 auto;
            width: 100%;
            justify-content: flex-end;
            gap: 1.25rem;
          }
        }

        /* Mobile: show only the address, centered. Hide phone + util
           actions (the drawer already surfaces those). Center the main
           bar's logo with the hamburger pinned right. */
        @media (max-width: 767px) {
          .header-top-strip-inner {
            justify-content: center;
            padding: 0.55rem 1rem;
          }
          .header-top-address-slot {
            flex: 0 1 auto;
            width: 100%;
            justify-content: center;
          }
          .header-top-address {
            justify-content: center;
            text-align: center;
            font-weight: 400;
            color: inherit;
          }
          .header-top-actions {
            display: none;
          }
          /* Center-align mobile logo: absolutely position the logo slot
             wrapper in the middle of the bar so the hamburger (which stays
             on the right via ml-auto) doesn't push it off-center. */
          .header-bar-row {
            position: relative;
            justify-content: center;
            min-height: 3rem;
          }
          .aion-header-logo-slot {
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            pointer-events: auto;
          }
        }

        /* Collapse the top strip on scroll — height animates to 0 so the
           main bar rides all the way up to the viewport top. */
        .header-scrolled .header-top-strip {
          max-height: 0;
          opacity: 0;
          padding-top: 0 !important;
          padding-bottom: 0;
          pointer-events: none;
        }

        /* ================================================================ */
        /* Main navigation bar                                             */
        /* ================================================================ */
        .header-bar {
          border-bottom: 1px solid transparent;
          background-color: transparent;
          transition: background-color 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease;
        }
        .header-scrolled .header-bar {
          background-color: #ffffff;
          border-bottom-color: var(--vibe-color-border);
          box-shadow:
            0 1px 3px rgba(0,0,0,0.04),
            0 4px 12px rgba(0,0,0,0.03);
        }
        .header-transparent .header-bar {
          border-bottom-color: transparent;
          box-shadow: none;
        }
        .header-bar-inner {
          padding-top: 1.1rem;
          padding-bottom: 1.1rem;
          padding-left: 1.5rem;
          padding-right: 1.5rem;
          max-width: 100%;
          box-sizing: border-box;
        }
        .header-bar-row {
          width: 100%;
          max-width: 100%;
          min-width: 0;
          gap: 0.5rem;
        }
        .aion-header-logo-slot {
          flex-shrink: 0;
          max-width: min(12rem, 28vw);
        }
        .header-bar-actions {
          flex-shrink: 0;
        }
        @media (min-width: 768px) {
          .header-bar-inner {
            padding-top: 1.25rem;
            padding-bottom: 1.25rem;
            padding-left: 3.125rem;
            padding-right: 3.125rem;
          }
        }
        @media (min-width: 1280px) {
          .header-bar-inner {
            padding-left: 4rem;
            padding-right: 4rem;
          }
        }
        .header-bar-logo {
          width: auto;
          height: auto;
          max-width: 10rem;
          max-height: 2.75rem;
          object-fit: contain;
        }
        @media (max-width: 767px) {
          .header-bar-logo {
            max-width: 110px;
            max-height: 3rem;
          }
        }
        @media (min-width: 768px) {
          .header-bar-logo {
            max-width: 11rem;
            max-height: 3rem;
          }
        }
        @media (min-width: 1024px) {
          .header-bar-logo {
            max-width: 10.5rem;
            max-height: 3rem;
          }
        }
        @media (min-width: 1280px) {
          .header-bar-logo {
            max-width: 11.5rem;
            max-height: 3.25rem;
          }
        }
        .header-bar-logo-stack {
          position: relative;
        }
        .header-bar-logo--dark {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          height: 100%;
          max-width: none;
          max-height: none;
          object-fit: contain;
          object-position: left center;
          opacity: 0;
          pointer-events: none;
        }
        .header-bar-logo--light,
        .header-bar-logo--dark {
          transition: opacity 0.3s ease;
        }
        /* Scrolled bar is white — swap to the dark logo. */
        .header-scrolled .header-bar-logo--light {
          opacity: 0;
        }
        .header-scrolled .header-bar-logo--dark {
          opacity: 1;
        }

        /* Single-asset fallback: no distinct dark logo exists (property
           configured only one variant, or both slots resolved to the same
           URL). Cross-fade a brightness(0) filter on scroll so the same
           asset renders as a solid dark silhouette on the white bar —
           preserves the logo's shape while guaranteeing contrast. */
        .header-bar-logo--auto-darken {
          transition: filter 0.3s ease;
        }
        .header-scrolled .header-bar-logo--auto-darken {
          filter: brightness(0);
        }

        /* Click-through for the transparent header so the hero content
           below stays interactive. Bar controls re-enable pointer events. */
        .header-transparent .header-bar {
          pointer-events: none;
        }
        .header-transparent .header-bar a,
        .header-transparent .header-bar button,
        .header-transparent .mobile-menu-panel {
          pointer-events: auto;
        }

        /* ================================================================ */
        /* Desktop horizontal navigation (xl+)                             */
        /* Shown only from 1280px up so logo + links + Schedule a Tour     */
        /* never spill past the viewport edge (the failure mode in the     */
        /* original screenshot). Below xl the hamburger + drawer carry nav.*/
        /* ================================================================ */
        .header-desktop-nav {
          gap: 0.1rem;
          min-width: 0;
          flex: 1 1 auto;
          justify-content: flex-end;
        }
        .header-nav-link {
          position: relative;
          display: inline-flex;
          align-items: center;
          padding: 0.5rem 0.5rem;
          font-family: var(--vibe-font-body);
          font-size: 0.8125rem;
          font-weight: 400;
          letter-spacing: 0.02em;
          color: #ffffff;
          text-decoration: none;
          white-space: nowrap;
          transition: color 0.2s ease, opacity 0.2s ease;
        }
        @media (min-width: 1440px) {
          .header-nav-link {
            padding: 0.5rem 0.65rem;
            font-size: 0.875rem;
          }
        }
        .header-nav-link:hover {
          opacity: 0.82;
        }
        .header-desktop-nav .header-nav-link--active::after {
          content: '';
          position: absolute;
          left: 0.5rem;
          right: 0.5rem;
          bottom: 0.15rem;
          height: 2px;
          border-radius: 2px;
          background: currentColor;
        }
        @media (min-width: 1440px) {
          .header-desktop-nav .header-nav-link--active::after {
            left: 0.65rem;
            right: 0.65rem;
          }
        }
        /* Scrolled state — white bar → dark text. */
        .header-scrolled .header-nav-link {
          color: var(--aion-header-scrolled-text, var(--vibe-color-text));
        }
        .header-scrolled .header-nav-link--active {
          color: var(--vibe-color-primary);
        }

        /* Parent nav item hover dropdown (white surface). */
        .header-nav-group {
          display: inline-flex;
        }
        .nav-more-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          min-width: 12rem;
          padding: 0.4rem 0;
          background: var(--vibe-color-background);
          border: 1px solid var(--vibe-color-border);
          border-radius: 2px;
          box-shadow: 0 10px 28px rgba(0, 0, 0, 0.14);
          opacity: 0;
          visibility: hidden;
          transform: translateY(6px);
          transition: opacity 0.2s ease, transform 0.2s ease, visibility 0s linear 0.2s;
          z-index: 60;
        }
        .header-nav-group:hover .nav-more-dropdown,
        .header-nav-group:focus-within .nav-more-dropdown {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
          transition: opacity 0.2s ease, transform 0.2s ease, visibility 0s linear 0s;
        }
        .nav-more-dropdown-link {
          display: block;
          padding: 0.5rem 1.1rem;
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          font-weight: 400;
          letter-spacing: 0.02em;
          color: var(--vibe-color-text);
          text-decoration: none;
          white-space: nowrap;
          transition: background 0.15s ease;
        }
        .nav-more-dropdown-link:hover {
          background: var(--vibe-color-surface);
        }

        /* ================================================================ */
        /* Schedule a Tour CTA — rounded, heading-font pill (matches the    */
        /* Footer's Resident Portal / Apply Now buttons so every button-    */
        /* style CTA across the theme reads as one family).                 */
        /* ================================================================ */
        .header-schedule-btn {
          display: none;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
          padding: 0.85rem 1.15rem;
          font-family: var(--vibe-font-heading, var(--vibe-font-body, sans-serif));
          font-size: 0.95rem;
          font-weight: 500;
          text-align: center;
          color: var(--aion-header-cta-on-accent, var(--vibe-color-text-on-secondary, #ffffff));
          background: var(--aion-header-cta-accent, var(--vibe-color-secondary, #c9a961));
          border-radius: 10px;
          text-decoration: none;
          white-space: nowrap;
          transition: background-color 0.25s ease, color 0.25s ease, opacity 0.25s ease;
        }
        @media (min-width: 640px) {
          .header-schedule-btn {
            display: inline-flex;
          }
        }
        @media (min-width: 1440px) {
          .header-schedule-btn {
            padding: 1.1rem 1.5rem;
            font-size: 1.1rem;
          }
        }
        .header-schedule-btn:hover {
          background: var(--comfy-cta-bg-hover, var(--vibe-color-surface, #f5f5f5));
          color: var(--comfy-cta-fg-hover, #1a1a1a);
        }

        /* ================================================================ */
        /* Hamburger button                                                */
        /* ================================================================ */
        .header-hamburger-bar {
          background-color: #ffffff;
        }
        .header-scrolled .header-hamburger-bar {
          background-color: var(--aion-header-scrolled-text, var(--vibe-color-text));
        }

        /* ================================================================ */
        /* Mobile scroll behavior                                          */
        /* ---------------------------------------------------------------- */
        /* Below 768px the header behaves differently on scroll:            */
        /*   • The address strip stays pinned to the nav (no collapse) so   */
        /*     the whole header travels as one sticky unit.                 */
        /*   • The main nav bar fills with the primary theme color instead  */
        /*     of the default white surface. The address strip keeps its    */
        /*     own #ebebeb band above it.                                   */
        /*   • The logo stays on its mono-white asset for contrast against  */
        /*     the primary fill.                                            */
        /*   • The hamburger stays white so the bars stay clearly visible   */
        /*     on the primary-colored background.                           */
        /* All transitions inherit the base durations from .header-bar /    */
        /* .header-bar-logo--* so the swap is smooth.                       */
        /* ================================================================ */
        @media (max-width: 767px) {
          /* Keep the address bar attached — undo the collapse animation. */
          .header-scrolled .header-top-strip {
            max-height: 200px;
            opacity: 1;
            padding-top: 0;
            padding-bottom: 0;
            pointer-events: auto;
          }
          /* Primary color fill on the main nav; drop the border/shadow so
             the address strip + nav read as one connected header. */
          .header-scrolled .header-bar {
            background-color: var(--vibe-color-primary);
            border-bottom-color: transparent;
            box-shadow: none;
          }
          /* Keep the light (mono-white) logo visible on scroll — reverses
             the desktop swap. */
          .header-scrolled .header-bar-logo--light {
            opacity: 1;
          }
          .header-scrolled .header-bar-logo--dark {
            opacity: 0;
          }
          /* Single-asset fallback: skip the brightness(0) filter so the
             mono-white mark stays legible on the primary fill. */
          .header-scrolled .header-bar-logo--auto-darken {
            filter: none;
          }
          /* Hamburger stays white for contrast on the primary fill. */
          .header-scrolled .header-hamburger-bar {
            background-color: #ffffff;
          }
        }

        /* ================================================================ */
        /* Mobile menu panel                                               */
        /* ================================================================ */
        .mobile-menu-panel {
          background-color: #0b0c10;
        }
        .mobile-menu-panel.mobile-menu-open {
          box-shadow: -16px 0 50px rgba(0, 0, 0, 0.4);
        }
        /* Lock drawer width for every non-mobile hamburger viewport
           (tablet through smaller desktop). Compact enough that nav +
           CTAs fill the sheet without a wide empty navy strip on the
           right; caps to the mobile peek margin on narrow tablets. */
        @media (min-width: 640px) and (max-width: 1279.98px) {
          .mobile-menu-panel {
            width: 22rem;
            max-width: calc(100% - 3rem);
          }
        }
        .menu-backdrop {
          cursor: pointer;
        }
        /* EliseAI (and similar) chat launchers sit at very high z-index and
           otherwise paint on top of the open hamburger drawer on mobile.
           Hide them only while the nav is open; restore when it closes. */
        body.header-menu-open .meetelise-chat,
        body.header-menu-open [class*="meetelise-chat"],
        body.header-menu-open iframe[src*="eliseai"],
        body.header-menu-open iframe[src*="meetelise"] {
          visibility: hidden !important;
          pointer-events: none !important;
        }
        .menu-panel-header {
          flex-shrink: 0;
        }
        .menu-layout {
          width: 100%;
        }
        .menu-nav-link {
          color: rgba(255, 255, 255, 0.95);
        }
        .menu-nav-link:hover {
          color: #fff;
        }
        .menu-nav-link--active {
          color: var(--vibe-color-secondary, #7ec8e3);
        }
        .menu-contact-row {
          display: flex;
          align-items: flex-start;
          gap: 0.85rem;
          color: rgba(255, 255, 255, 0.95);
          text-decoration: none;
          transition: color 0.2s ease;
        }
        .menu-contact-row:hover {
          color: #fff;
        }
        .menu-contact-icon-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 2rem;
          height: 2rem;
          flex-shrink: 0;
          color: #fff;
        }
        .menu-contact-icon-badge svg {
          width: 1.125rem;
          height: 1.125rem;
        }
        .menu-contact-text {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          font-weight: 600;
          line-height: 1.45;
        }
        @media (min-width: 768px) {
          .menu-contact-text {
            font-size: 1.125rem;
          }
        }
        .mobile-menu-panel .menu-bg {
          position: absolute;
          inset: 0;
          z-index: 0;
        }
        .mobile-menu-panel .menu-overlay {
          position: absolute;
          inset: 0;
          z-index: 1;
          background: rgba(0, 0, 0, 0.82);
        }
        .mobile-menu-panel .menu-close-btn {
          position: static;
          width: 2.75rem;
          height: 2.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(255, 255, 255, 0.85);
          background: transparent;
          border: none;
          cursor: pointer;
          transition: color 0.2s ease, transform 0.2s ease;
        }
        .mobile-menu-panel .menu-close-btn:hover {
          color: #fff;
          transform: rotate(90deg);
        }
        .mobile-menu-panel .menu-close-btn svg {
          width: 1.75rem;
          height: 1.75rem;
        }

        .mobile-menu-panel a,
        .mobile-menu-panel button {
          color: rgba(255, 255, 255, 0.92);
        }
        .mobile-menu-panel a:hover {
          color: #fff;
        }
        .mobile-menu-panel nav a {
          border-bottom-color: transparent;
        }

        /* Underline-on-hover for drawer contact rows. */
        .vantage-nav-darken-hover {
          text-decoration-line: underline;
          text-decoration-color: transparent;
          text-decoration-thickness: 1.5px;
          text-underline-offset: 6px;
          transition: text-decoration-color 0.25s ease, text-underline-offset 0.25s ease, color 0.2s ease;
        }
        .vantage-nav-darken-hover:hover {
          text-decoration-color: currentColor;
          text-underline-offset: 4px;
        }
        .mobile-menu-panel a:hover .vantage-nav-darken-hover,
        .mobile-menu-panel a:focus-visible .vantage-nav-darken-hover {
          text-decoration-color: currentColor;
          text-underline-offset: 4px;
        }

        /* ================================================================ */
        /* Solid primary drawer + single-column layout (all widths).        */
        /* The drawer surface is the site's primary theme color per the     */
        /* reference; nav links, CTAs, and the contact block all pick       */
        /* colors that stay legible on that primary fill.                   */
        /* ================================================================ */
        @media (min-width: 0px) {
          .mobile-menu-panel {
            background-color: var(--vibe-color-primary);
          }
          .mobile-menu-panel .menu-bg,
          .mobile-menu-panel .menu-overlay {
            display: none;
          }
          /* Contact column stacks below the nav for the entire
             hamburger range (mobile through smaller desktop). */
          .mobile-menu-panel .menu-nav {
            max-width: none;
            flex: 1 1 auto;
          }
          .mobile-menu-panel .menu-layout {
            min-height: 0;
            padding-top: 0.5rem;
          }
          .mobile-menu-panel .menu-scroll {
            overflow-y: auto;
            min-height: 0;
            padding-bottom: 1.5rem;
          }
          /* Nav links — one size for the whole hamburger range so
             tablet and smaller desktop match the smaller-desktop look. */
          .mobile-menu-panel .menu-nav-link {
            position: relative;
            display: block;
            width: 100%;
            font-family: var(--vibe-font-body);
            font-size: 1.0625rem;
            line-height: 1.3;
            font-weight: 400;
            padding-top: 0.55rem;
            padding-bottom: 0.55rem;
            color: rgba(255, 255, 255, 0.92);
            text-decoration: none;
            transition: color 0.25s ease;
          }
          /* The inline-flex label wrapper anchors the underline — using
             position:relative on this span means the ::after can span
             100% of the label's natural width, which reads as a much
             more prominent side-underline than a fixed 2.25rem bar
             (per the reference's "Amenities" state). */
          .mobile-menu-panel .menu-nav-link .menu-nav-link__label {
            position: relative;
            padding-bottom: 0.35rem;
          }
          .mobile-menu-panel .menu-nav-link .menu-nav-link__label::after {
            content: '';
            position: absolute;
            left: 0;
            right: 0;
            bottom: 0;
            height: 2px;
            border-radius: 2px;
            background: #ffffff;
            transform-origin: left center;
            transform: scaleX(0);
            opacity: 0;
            transition: transform 0.28s ease, opacity 0.22s ease;
            pointer-events: none;
          }
          .mobile-menu-panel .menu-nav-link:hover,
          .mobile-menu-panel .menu-nav-link:focus-visible {
            color: #fff;
          }
          .mobile-menu-panel .menu-nav-link:hover .menu-nav-link__label::after,
          .mobile-menu-panel .menu-nav-link:focus-visible .menu-nav-link__label::after,
          .mobile-menu-panel .menu-nav-link--active .menu-nav-link__label::after {
            transform: scaleX(1);
            opacity: 1;
          }
          .mobile-menu-panel .menu-nav-link--active {
            color: #fff;
          }
          /* CTA stack — three visually-identical muted buttons matching
             the reference. Base .menu-drawer-cta carries the shared
             layout; --primary / --secondary variants exist but resolve
             to the same surface so future differentiation (if the site
             ever wants a highlighted primary) doesn't require another
             JSX change. */
          .mobile-menu-panel .menu-drawer-cta-row {
            display: flex;
            flex-direction: column;
            gap: 0.65rem;
            margin-top: 1.25rem;
            max-width: none;
          }
          .mobile-menu-panel .menu-drawer-cta {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            padding: 0.95rem 1.5rem;
            font-family: var(--vibe-font-body);
            font-size: 1.05rem;
            font-weight: 400;
            letter-spacing: 0.005em;
            line-height: 1.2;
            text-transform: none;
            text-decoration: none;
            border-radius: 999px;
            border: 1px solid transparent;
            /* Match the Hero CTA color palette — solid secondary fill with
               the on-secondary foreground token, hover swaps to the surface
               color with auto-contrast text (same swap .comfy-cta uses). */
            background: var(--vibe-color-secondary, #c9a961);
            color: var(--vibe-color-text-on-secondary, #ffffff);
            transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, transform 0.15s ease;
          }
          .mobile-menu-panel .menu-drawer-cta:hover,
          .mobile-menu-panel .menu-drawer-cta:focus-visible {
            background: var(--comfy-cta-bg-hover, var(--vibe-color-surface, #f5f5f5));
            color: var(--comfy-cta-fg-hover, #1a1a1a);
            transform: translateY(-1px);
          }
          .mobile-menu-panel .menu-drawer-cta--primary,
          .mobile-menu-panel .menu-drawer-cta--secondary {
            /* Reserved for future variants; both currently inherit the
               muted-grey base per the reference. */
          }

          /* Non-mobile hamburger drawer: CTAs span the content column so
             they don't leave a second empty strip inside the sheet. */
          @media (min-width: 640px) {
            .mobile-menu-panel .menu-drawer-cta-row {
              align-items: stretch;
            }
            .mobile-menu-panel .menu-drawer-cta {
              width: 100%;
              min-width: 0;
              max-width: none;
              justify-content: center;
            }
          }

          /* ============================================================ */
          /* Contact column — Call Us / Visit Us / Office Hours           */
          /* ============================================================ */
          .mobile-menu-panel .menu-contact {
            display: block;
            margin-top: 1.5rem;
            padding: 0;
            color: #ffffff;
          }
          .mobile-menu-panel .menu-info-stack {
            display: flex;
            flex-direction: column;
            gap: 1.4rem;
          }
          .mobile-menu-panel .menu-info-block {
            display: flex;
            flex-direction: column;
            gap: 0.35rem;
          }
          .mobile-menu-panel .menu-info-label {
            font-family: var(--vibe-font-body);
            font-size: 1.05rem;
            font-weight: 700;
            color: #ffffff;
            line-height: 1.2;
          }
          .mobile-menu-panel .menu-info-value {
            font-family: var(--vibe-font-body);
            font-size: 0.95rem;
            line-height: 1.45;
            color: rgba(255, 255, 255, 0.92);
            text-decoration: none;
            transition: color 0.2s ease;
          }
          .mobile-menu-panel .menu-info-value:hover,
          .mobile-menu-panel .menu-info-value:focus-visible {
            color: #ffffff;
          }
          .mobile-menu-panel .menu-info-value--strong {
            font-family: var(--vibe-font-heading, var(--vibe-font-body));
            font-size: 1.35rem;
            font-weight: 800;
            letter-spacing: 0.005em;
            line-height: 1.15;
          }
          .mobile-menu-panel .menu-info-hours {
            position: relative;
          }
          .mobile-menu-panel .menu-hours-list {
            display: flex;
            flex-direction: column;
            gap: 0.4rem;
            margin: 0;
            padding: 0;
          }
          .mobile-menu-panel .menu-hours-row {
            display: flex;
            align-items: baseline;
            justify-content: space-between;
            gap: 0.75rem;
            font-family: var(--vibe-font-body);
            font-size: 0.9rem;
            color: rgba(255, 255, 255, 0.88);
          }
          .mobile-menu-panel .menu-hours-day {
            font-weight: 500;
            white-space: nowrap;
          }
          .mobile-menu-panel .menu-hours-time {
            margin: 0;
            font-weight: 500;
            text-align: right;
            white-space: nowrap;
          }

        }

      `}</style>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {},
  slotGroups: {
    'header.logo': {
      label: 'Logo',
      fields: {
        image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_white_logo', suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'header.apply_now_link': {
      label: 'Apply Now Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/apply-now' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.resident_access_link': {
      label: 'Resident Access Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Portal' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.schedule_tour_link': {
      label: 'Schedule Tour Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.address_link': {
      label: 'Address Link',
      fields: {
        text: {
          label: 'Address',
          type: 'text',
          defaultField: 'property_full_address',
          suggestedFields: ['property_full_address', 'property_address_1'],
          defaultValue: '123 Main Street',
        },
        url: { label: 'URL', type: 'url' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.phone_link': {
      label: 'Phone Link',
      fields: {
        text: {
          label: 'Phone Text',
          type: 'text',
          defaultField: 'property_call_tracking_phone',
        },
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_call_tracking_phone',
          suggestedFields: ['property_call_tracking_phone', 'property_phone'],
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
  collectionBindings: {
    'header.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
    'header.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Header as any).contentSchema = contentSchema;
